import { computedFrom, Disposable, observable } from "aurelia-binding";
import { Reflection } from "../../../utilities/reflection";
import { ItemSource, DataGridColumn } from "../../../models/queries/queries.models";
import { DataGrid } from "../../components/data-grid/data-grid";
import { I18N } from "aurelia-i18n";
import { BindingEngine } from "aurelia-framework";
import { FromNowValueConverter } from "../../components/converters/date/date-format";

export class CustomGridPageBase {

    private static _cache: { [key: string]: { fields: any, pageCount: number, sortingColumns: DataGridColumn[] }; } = {};
    private _cols: DataGridColumn[] = [];

    public itemSource: ItemSource | ((x) => ItemSource);

    @computedFrom('_columns')
    public get columns(): DataGridColumn[] {
        return this._cols;
    }

    public set columns(cols: DataGridColumn[]) {
        this._cols = cols;
    }

    public initialSorting: DataGridColumn[] = [];

    @observable()
    public dataGridVM: DataGrid;

    public defaultPageCount: number = 10;

    public isLoading: boolean;

    private subscriptionForIsLoadingChanges: Disposable;

    constructor(protected i18N: I18N, protected itemsName: string = "Items", protected bindingEngine: BindingEngine) { }

    dataGridVMChanged() {
        if (this.dataGridVM) {
            this.dataGridVM.pageCount = this.defaultPageCount;
            this.subscriptionForIsLoadingChanges = this.bindingEngine.propertyObserver(this.dataGridVM, 'isLoading').subscribe(x => {
                this.isLoading = x;
            })
        }
    }

    public pageCountSet: any[] = [
        { label: 10, value: 10 },
        { label: 50, value: 50 },
        { label: 100, value: 100 },
    ];

    setPageCount(pageCount: { label: string, value: number }) {
        this.dataGridVM.pageCount = pageCount.value;
    }

    @computedFrom(Reflection.getPropertyPath<CustomGridPageBase>(x => x.dataGridVM.itemSource), Reflection.getPropertyPath<CustomGridPageBase>(x => x.dataGridVM.itemSource.source),
        Reflection.getPropertyPath<CustomGridPageBase>(x => x.dataGridVM.page), Reflection.getPropertyPath<CustomGridPageBase>(x => x.dataGridVM.pageCount),
        Reflection.getPropertyPath<CustomGridPageBase>(x => x.dataGridVM.itemSource.source.length), Reflection.getPropertyPath<CustomGridPageBase>(x => x.dataGridVM.unfilteredCount),
        Reflection.getPropertyPath<CustomGridPageBase>(x => x.itemsName))
    public get displayingItemsText() {
        if (this.dataGridVM) {
            let unfilteredCount = '0';
            if (this.dataGridVM.itemSource && Array.isArray(this.dataGridVM.itemSource.source) && this.dataGridVM.itemSource.source.length)
                unfilteredCount = this.dataGridVM.itemSource.source.length.toString();
            else if (this.dataGridVM.unfilteredCount && this.dataGridVM.unfilteredCount > 0)
                unfilteredCount = this.dataGridVM.unfilteredCount.toString();
            const currentCount = (this.dataGridVM.page + 1) * this.dataGridVM.pageCount;
            const current = currentCount > Number(unfilteredCount) ? unfilteredCount : currentCount.toString();
            return this.i18N.tr('displaying-items-text').replace("{0}", (this.dataGridVM.page == 0 ? 1 : (this.dataGridVM.page * this.dataGridVM.pageCount) + 1).toString()).replace("{1}", current).replace("{2}", unfilteredCount).replace("{3}", this.itemsName);
        }
        else {
            return "";
        }
    }

    detached() {
        this.subscriptionForIsLoadingChanges?.dispose();

        if (this["__proto__"]._cachableProperties) {

            if (!CustomGridPageBase._cache[this.itemsName]) {
                CustomGridPageBase._cache[this.itemsName] = { fields: {}, pageCount: undefined, sortingColumns: undefined };
            }

            const cache = CustomGridPageBase._cache[this.itemsName];
            this["__proto__"]._cachableProperties.forEach(key => {
                cache.fields[key] = this[key];
            });

            cache.pageCount = this.dataGridVM.pageCount;

            cache.sortingColumns = this.columns.filter(x => x.isSortable);
        }
    }

    applyCache(): boolean {
        const cache = CustomGridPageBase._cache[this.itemsName];
        if (cache) {
            this["__proto__"]._cachableProperties.forEach(key => {
                this[key] = cache.fields[key];
            });

            if (cache.pageCount !== undefined) {
                this.defaultPageCount = cache.pageCount;
            }

            if (cache.pageCount !== undefined) {
                this.defaultPageCount = cache.pageCount;
            }

            if (cache.sortingColumns) {
                cache.sortingColumns.forEach(sortingCol => {
                    const column = this.columns.find(x => x.name == sortingCol.name);
                    if (column) {
                        column.isSortable = true;
                        column.isAscending = sortingCol.isAscending;
                    }
                });
            }

            return true;
        }
        return false;
    }

    public getDateToString(date: Date | string, useNa: boolean = true): string {
        if (!date)
            return useNa ? 'N/A' : '';

        let val: Date;

        if (typeof date == 'string')
            val = new Date(date);
        else
            val = date

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return useNa ? 'N/A' : '';

        return new FromNowValueConverter().toView(val);
    }
}
